/**
 * Pass-and-play setup screen.
 *
 * Two players share one device. P1 picks their name + deck \u2014 they then
 * physically hand the device to P2 (we gate this behind a PassScreen) \u2014 P2
 * picks their name + deck \u2014 device passes back to P1 to begin the match.
 *
 * The game is set up via the existing setupGame() and then decorated with a
 * `passAndPlay` block. Initial viewerNum is the coin-flip winner; coin flip
 * itself is handled by the board (existing CoinFlipDialog flow).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { useStore } from "@/hooks/useStore";
import type { Card } from "@/types/card";
import type { Deck } from "@/types/deck";
import type { GameState, ScreenLayout, PassAndPlayState } from "@/types/game";
import { setupGame } from "@/game/engine";
import { Swords, AlertCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PassScreen } from "@/components/playtest/PassScreen";

type Step = "p1-pick" | "pass-to-p2" | "p2-pick" | "pass-back" | "ready";

function buildCardMap(cards: Card[]): Map<string, Card> {
  const m = new Map<string, Card>();
  for (const c of cards) m.set(c.id, c);
  return m;
}

export function PlaytestPassAndPlay() {
  const { state, ready } = useStore();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("p1-pick");
  const [name1, setName1] = useState("Player 1");
  const [name2, setName2] = useState("Player 2");
  const [deck1Id, setDeck1Id] = useState<string>("");
  const [deck2Id, setDeck2Id] = useState<string>("");
  const [layout, setLayout] = useState<ScreenLayout>("stacked");
  const [peek, setPeek] = useState(false);

  const cardMap = useMemo(() => buildCardMap(state.cards), [state.cards]);
  const decks = state.decks;
  const legalDecks = decks.filter((d) => d.legality.status === "legal");
  const enoughDecks = legalDecks.length >= 1;

  function pickerPanel(
    playerLabel: string,
    name: string,
    setName: (v: string) => void,
    deckId: string,
    setDeckId: (v: string) => void,
  ) {
    return (
      <Panel className="space-y-3">
        <h2 className="font-display text-base font-semibold text-[#e2e8f0]">
          {playerLabel}
        </h2>
        <label className="block">
          <span className="gm-label">Name</span>
          <input
            className="gm-input mt-1 w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={playerLabel}
          />
        </label>
        <label className="block">
          <span className="gm-label">Deck</span>
          <select
            className="gm-input mt-1 w-full"
            value={deckId}
            onChange={(e) => setDeckId(e.target.value)}
          >
            <option value="">Select a deck</option>
            {decks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.deckName}
                {d.legality.status !== "legal" ? ` \u2014 ${d.legality.status}` : ""}
              </option>
            ))}
          </select>
        </label>
      </Panel>
    );
  }

  function start() {
    const d1: Deck | undefined =
      decks.find((d) => d.id === deck1Id) ?? legalDecks[0] ?? decks[0];
    const d2: Deck | undefined =
      decks.find((d) => d.id === deck2Id) ?? legalDecks[0] ?? decks[0];
    if (!d1 || !d2) {
      toast.error("Build at least one deck first");
      return;
    }
    const baseGs: GameState = setupGame(
      name1.trim() || "Player 1",
      name2.trim() || "Player 2",
      d1,
      d2,
      cardMap,
    );
    // Initial viewer is P1 (they ran setup). Coin-flip dialog will appear for
    // them on the board; subsequent priority handoffs are gated by the
    // PassScreen logic in PlaytestBoard.
    const passAndPlay: PassAndPlayState = {
      mode: "pass-and-play",
      layout,
      peek,
      viewerNum: 1,
      pendingPassTo: null,
      declinedReactionEntryIds: [],
    };
    const gs: GameState = { ...baseGs, passAndPlay };
    navigate("/playtest/board", { state: { game: gs } });
  }

  if (!ready) {
    return <p className="p-6 text-sm text-[#64748b]">Loading…</p>;
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  if (step === "pass-to-p2") {
    return (
      <PassScreen
        toPlayerName={name2.trim() || "Player 2"}
        layout={layout}
        onReady={() => setStep("p2-pick")}
      />
    );
  }
  if (step === "pass-back") {
    return (
      <PassScreen
        toPlayerName={name1.trim() || "Player 1"}
        layout={layout}
        onReady={() => start()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <PageTitle>Pass-and-Play</PageTitle>
        <p className="mt-1 text-sm text-[#64748b]">
          Two players, one device. The screen passes back and forth so each
          player only sees their own hand.
        </p>
      </div>

      {!enoughDecks && (
        <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            You need at least 1 legal deck to start a match. Build and finalize
            decks in the{" "}
            <Link to="/decks" className="underline hover:text-red-300">
              Decks
            </Link>{" "}
            section.
          </span>
        </div>
      )}

      {/* Match-wide settings (visible only on P1's setup so both players agree) */}
      {step === "p1-pick" && (
        <Panel className="space-y-3">
          <h3 className="font-display text-sm font-semibold">Match settings</h3>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <span className="gm-label">Screen layout</span>
            <div className="flex gap-2">
              <Button
                variant={layout === "stacked" ? "primary" : "secondary"}
                onClick={() => setLayout("stacked")}
              >
                Stacked (active player on bottom)
              </Button>
              <Button
                variant={layout === "tabletop" ? "primary" : "secondary"}
                onClick={() => setLayout("tabletop")}
              >
                Tabletop (board flat between players)
              </Button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={peek}
              onChange={(e) => setPeek(e.target.checked)}
            />
            Peek mode (debug) — reveal both hands while tuning the engine
          </label>
        </Panel>
      )}

      {step === "p1-pick" &&
        pickerPanel("Player 1", name1, setName1, deck1Id, setDeck1Id)}
      {step === "p2-pick" &&
        pickerPanel("Player 2", name2, setName2, deck2Id, setDeck2Id)}

      <div className="max-w-2xl">
        {step === "p1-pick" && (
          <Button
            onClick={() => setStep("pass-to-p2")}
            disabled={!enoughDecks || !name1.trim() || !deck1Id}
            className="w-full gap-2"
          >
            <ArrowRight size={14} /> Continue — pass to {name2 || "Player 2"}
          </Button>
        )}
        {step === "p2-pick" && (
          <Button
            onClick={() => setStep("pass-back")}
            disabled={!enoughDecks || !name2.trim() || !deck2Id}
            className="w-full gap-2"
          >
            <Swords size={14} /> Begin match
          </Button>
        )}
      </div>
    </div>
  );
}
